import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const article = getArticleBySlug(articleSlug || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
                <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
              </div>
              <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">你访问的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    url: `https://hmanga.example.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'h漫导航',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          {/* Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: article.categoryName, path: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </section>

          {/* Article Content */}
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            {/* Meta */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                {article.featured && (
                  <span className="px-3 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    精选推荐
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次浏览
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content Body */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-1.5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-600 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-16 md:py-20">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">
                  相关h漫推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:shadow-md transition-all cursor-pointer block"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2 leading-snug">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span>{related.date}</span>
                          <span>{related.readTime} 分钟阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}